import { useState } from 'react'
import { Button, ButtonGroup, Divider, Menu, MenuItem, Popover, Tab, Tabs } from '@blueprintjs/core'
import type { IconName } from '@blueprintjs/icons'
import { Link } from 'react-router-dom'

import type { Store } from '../store'

type RibbonTab = 'part' | 'cell' | 'operations' | 'simulate' | 'output'
type GroupBtn = { icon: IconName; label: string; onClick?: () => void; disabled?: boolean }
type Group = { title: string; buttons: GroupBtn[] }

/**
 * Top ribbon — SolidWorks-style tabbed command set.
 *
 * Groups wire to the store via onClick. Phase 1 mostly stubs these; later
 * phases replace the stubs with real actions (open STEP, generate, etc.).
 * The small File menu on the left stays for familiarity.
 */
export function Ribbon({ store }: { store: Store }) {
  const [tab, setTab] = useState<RibbonTab>('part')

  const groups: Record<RibbonTab, Group[]> = {
    part: [
      {
        title: 'File',
        buttons: [
          { icon: 'folder-open', label: 'Open STEP…', onClick: () => store.openStepDialog() },
          { icon: 'eraser', label: 'Clear part', onClick: () => store.clearPart(), disabled: !store.job.part },
        ],
      },
      {
        title: 'Select',
        buttons: [
          { icon: 'selection', label: 'Faces' },
          { icon: 'edit', label: 'Edges' },
          { icon: 'path-search', label: 'Measure' },
        ],
      },
    ],
    cell: [
      {
        title: 'Robot cell',
        buttons: [
          { icon: 'cube', label: 'Robot' },
          { icon: 'rotate-page', label: 'Positioner' },
          { icon: 'wrench', label: 'Tool…', onClick: () => store.openToolImport() },
          { icon: 'map-marker', label: 'Work object' },
        ],
      },
    ],
    operations: [
      {
        title: 'Bulk (auto)',
        buttons: [
          {
            // One click = classify + create one Clad op covering every
            // outside face. For a worm this is nine times out of ten
            // what the user wants; split from the op afterwards if a
            // face needs its own weld params.
            icon: 'layers',
            label: 'Clad outside',
            onClick: () => store.newCladBulk('worm'),
            disabled: !store.job.part,
          },
          {
            icon: 'layers',
            label: 'Clad auger',
            onClick: () => store.newCladBulk('auger'),
            disabled: !store.job.part,
          },
        ],
      },
      {
        // Phase 2D presets for augers on a 2-axis positioner. Each
        // creates one Clad op with a different ``positioner_tilt_deg``
        // — top of flight at 0°, bottom at -90°. Both end up in the
        // same RAPID module with different eax_b values. Only useful
        // once the cell declares a tilt_mech_unit; otherwise the tilt
        // is ignored and the two presets degenerate to the same op.
        title: 'Flight (2-axis)',
        buttons: [
          {
            icon: 'arrow-up',
            label: 'Top of flight',
            onClick: () =>
              store.newCladBulk('auger', 0, 'Clad TOP of flight'),
            disabled: !store.job.part,
          },
          {
            icon: 'arrow-down',
            label: 'Bottom of flight',
            onClick: () =>
              store.newCladBulk('auger', -90, 'Clad BOTTOM of flight'),
            disabled: !store.job.part,
          },
        ],
      },
      {
        title: 'Manual',
        buttons: [
          {
            icon: 'add',
            label: 'Clad face',
            onClick: () => store.newOperation('clad'),
            disabled: !store.job.part,
          },
          {
            icon: 'merge-links',
            label: 'Seam weld',
            onClick: () => store.newOperation('seam'),
            disabled: !store.job.part,
          },
          { icon: 'exclude-row', label: 'Crack fill', disabled: true },
          { icon: 'grid-view', label: 'Pattern', disabled: true },
        ],
      },
    ],
    simulate: [
      {
        title: 'Compute',
        buttons: [
          {
            icon: 'build',
            label: 'Generate',
            onClick: () => store.generateAll(),
            disabled: store.job.operations.length === 0,
          },
          { icon: 'play', label: 'Play', disabled: !store.hasAnyTrajectory },
          { icon: 'step-forward', label: 'Step', disabled: !store.hasAnyTrajectory },
        ],
      },
      {
        title: 'Check',
        buttons: [
          { icon: 'warning-sign', label: 'Collisions', disabled: !store.hasAnyTrajectory },
          { icon: 'eye-open', label: 'Reach', disabled: !store.hasAnyTrajectory },
        ],
      },
    ],
    output: [
      {
        title: 'Export',
        buttons: [
          {
            icon: 'export',
            label: 'toolpath.json',
            onClick: () => store.exportArtifact('toolpath'),
            disabled: !store.hasAnyToolpath,
          },
          {
            icon: 'export',
            label: 'trajectory.json',
            onClick: () => store.exportArtifact('trajectory'),
            disabled: !store.hasAnyTrajectory,
          },
          {
            icon: 'export',
            label: 'collision.json',
            onClick: () => store.exportArtifact('collision'),
            disabled: !store.hasAnyTrajectory,
          },
          {
            icon: 'floppy-disk',
            label: 'RAPID (.mod)',
            onClick: () => store.exportArtifact('rapid'),
            disabled: !store.hasAnyTrajectory || !store.job.cell.tool || store.job.cell.tool.mass_kg == null,
          },
        ],
      },
    ],
  }

  const fileMenu = (
    <Menu>
      <MenuItem icon="folder-open" text="Open STEP…" onClick={() => store.openStepDialog()} />
      <MenuItem icon="floppy-disk" text="Save job…" onClick={() => store.saveJob()} />
      <MenuItem icon="document-open" text="Open job…" onClick={() => store.openJobDialog()} />
      <MenuItem icon="export" text="Export…" />
      <MenuItem icon="cog" text="Preferences…" onClick={() => store.openPrefs()} />
      <MenuItem icon="info-sign" text="About AMATL" onClick={() => store.openAbout()} />
    </Menu>
  )

  return (
    <div className="ribbon">
      <div className="ribbon-brand">
        <Popover content={fileMenu} placement="bottom-start">
          <Button icon="menu" minimal />
        </Popover>
        <span className="ribbon-title">AMATL</span>
        <Link to="/projects" className="ribbon-projects-link" title="Batch projects">
          <Button icon="folder-close" small minimal>Projects</Button>
        </Link>
        <Link to="/cells" className="ribbon-projects-link" title="Cell library">
          <Button icon="cube" small minimal>Cells</Button>
        </Link>
        <Button
          small
          minimal
          icon={store.preferences.theme === 'dark' ? 'flash' : 'moon'}
          title={store.preferences.theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={() =>
            store.setPreferences({
              ...store.preferences,
              theme: store.preferences.theme === 'dark' ? 'light' : 'dark',
            })
          }
        />
      </div>
      <Tabs id="ribbon-tabs" selectedTabId={tab} onChange={(id) => setTab(id as RibbonTab)}>
        <Tab id="part" title="Part" />
        <Tab id="cell" title="Cell" />
        <Tab id="operations" title="Operations" />
        <Tab id="simulate" title="Simulate" />
        <Tab id="output" title="Output" />
      </Tabs>
      <div className="ribbon-groups">
        {groups[tab].map((g, i) => (
          <RibbonGroup key={g.title} group={g} first={i === 0} />
        ))}
      </div>
    </div>
  )
}

function RibbonGroup({ group, first }: { group: Group; first: boolean }) {
  return (
    <>
      {!first && <Divider />}
      <div className="ribbon-group">
        <ButtonGroup minimal>
          {group.buttons.map((b) => (
            <Button
              key={b.label}
              icon={b.icon}
              text={b.label}
              onClick={b.onClick}
              disabled={b.disabled}
              small
            />
          ))}
        </ButtonGroup>
        <div className="ribbon-group-title">{group.title}</div>
      </div>
    </>
  )
}
